import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MapPin,
  Users,
  DollarSign,
  Award,
  BookOpen,
  Heart,
  Star,
  Filter,
  Search,
  Plus,
  Eye,
  Bookmark,
  TrendingUp,
  BarChart3,
  GraduationCap,
  Globe,
  Calendar,
  Target,
  CheckCircle
} from 'lucide-react';
import { Card } from '../components/shared/Card';
import { Button } from '../components/shared/Button';
import { Badge } from '../components/shared/Badge';
import { ProgressBar } from '../components/shared/ProgressBar';

interface College {
  id: string;
  name: string;
  location: string;
  state: string;
  type: 'public' | 'private';
  size: 'small' | 'medium' | 'large';
  ranking: number;
  acceptanceRate: number;
  tuition: number;
  averageGPA: number;
  averageSAT: number;
  programs: string[];
  specialties: string[];
  campusLife: string[];
  financialAid: number;
  graduationRate: number;
  employmentRate: number;
  diversity: number;
  researchOpportunities: boolean;
  studyAbroad: boolean;
  honors: boolean;
  distance: number;
  matchScore: number;
  applied: boolean;
  favorited: boolean;
  visited: boolean;
  image: string;
}

const colleges: College[] = [
  {
    id: '1',
    name: 'Massachusetts Institute of Technology',
    location: 'Cambridge, MA',
    state: 'Massachusetts',
    type: 'private',
    size: 'medium',
    ranking: 2,
    acceptanceRate: 4.1,
    tuition: 57590,
    averageGPA: 4.17,
    averageSAT: 1570,
    programs: ['Engineering', 'Computer Science', 'Physics', 'Mathematics', 'Economics'],
    specialties: ['Artificial Intelligence', 'Robotics', 'Biotechnology', 'Entrepreneurship'],
    campusLife: ['Research Opportunities', 'Innovation Labs', 'Tech Startups', 'Collaborative Culture'],
    financialAid: 73,
    graduationRate: 95,
    employmentRate: 89,
    diversity: 85,
    researchOpportunities: true,
    studyAbroad: true,
    honors: true,
    distance: 150,
    matchScore: 95,
    applied: false,
    favorited: true,
    visited: false,
    image: '🏛️'
  },
  {
    id: '2',
    name: 'Stanford University',
    location: 'Stanford, CA',
    state: 'California',
    type: 'private',
    size: 'medium',
    ranking: 5,
    acceptanceRate: 3.9,
    tuition: 61731,
    averageGPA: 4.18,
    averageSAT: 1570,
    programs: ['Computer Science', 'Engineering', 'Business', 'Medicine', 'Psychology'],
    specialties: ['Silicon Valley Connections', 'Entrepreneurship', 'Innovation', 'Venture Capital'],
    campusLife: ['Beautiful Campus', 'Tech Culture', 'Startup Incubators', 'Athletic Excellence'],
    financialAid: 78,
    graduationRate: 94,
    employmentRate: 93,
    diversity: 82,
    researchOpportunities: true,
    studyAbroad: true,
    honors: true,
    distance: 3000,
    matchScore: 88,
    applied: true,
    favorited: true,
    visited: true,
    image: '🌲'
  },
  {
    id: '3',
    name: 'University of California, Berkeley',
    location: 'Berkeley, CA',
    state: 'California',
    type: 'public',
    size: 'large',
    ranking: 20,
    acceptanceRate: 11.4,
    tuition: 14312,
    averageGPA: 4.25,
    averageSAT: 1520,
    programs: ['Engineering', 'Computer Science', 'Business', 'Environmental Science', 'Political Science'],
    specialties: ['Public Policy', 'Environmental Research', 'Tech Innovation', 'Social Impact'],
    campusLife: ['Diverse Community', 'Research Excellence', 'Bay Area Access', 'Activist Culture'],
    financialAid: 68,
    graduationRate: 92,
    employmentRate: 88,
    diversity: 90,
    researchOpportunities: true,
    studyAbroad: true,
    honors: true,
    distance: 2800,
    matchScore: 82,
    applied: false,
    favorited: false,
    visited: false,
    image: '🐻'
  },
  {
    id: '4',
    name: 'Georgia Institute of Technology',
    location: 'Atlanta, GA',
    state: 'Georgia',
    type: 'public',
    size: 'large',
    ranking: 35,
    acceptanceRate: 17.0,
    tuition: 12852,
    averageGPA: 4.07,
    averageSAT: 1460,
    programs: ['Engineering', 'Computer Science', 'Business', 'Architecture', 'Sciences'],
    specialties: ['Technology Innovation', 'Co-op Programs', 'Industry Partnerships', 'Research'],
    campusLife: ['Tech Hub', 'Internship Opportunities', 'Modern Facilities', 'Urban Campus'],
    financialAid: 72,
    graduationRate: 88,
    employmentRate: 91,
    diversity: 78,
    researchOpportunities: true,
    studyAbroad: true,
    honors: true,
    distance: 800,
    matchScore: 79,
    applied: false,
    favorited: true,
    visited: false,
    image: '🏗️'
  },
  {
    id: '5',
    name: 'University of Michigan',
    location: 'Ann Arbor, MI',
    state: 'Michigan',
    type: 'public',
    size: 'large',
    ranking: 25,
    acceptanceRate: 20.2,
    tuition: 17786,
    averageGPA: 3.90,
    averageSAT: 1450,
    programs: ['Engineering', 'Business', 'Medicine', 'Law', 'Liberal Arts'],
    specialties: ['Research Excellence', 'School Spirit', 'Alumni Network', 'Comprehensive Programs'],
    campusLife: ['Big Ten Athletics', 'Research Opportunities', 'Vibrant Campus', 'Strong Alumni'],
    financialAid: 65,
    graduationRate: 93,
    employmentRate: 90,
    diversity: 75,
    researchOpportunities: true,
    studyAbroad: true,
    honors: true,
    distance: 600,
    matchScore: 76,
    applied: false,
    favorited: false,
    visited: true,
    image: '🏈'
  },
  {
    id: '6',
    name: 'Carnegie Mellon University',
    location: 'Pittsburgh, PA',
    state: 'Pennsylvania',
    type: 'private',
    size: 'medium',
    ranking: 28,
    acceptanceRate: 13.2,
    tuition: 63829,
    averageGPA: 3.95,
    averageSAT: 1520,
    programs: ['Computer Science', 'Engineering', 'Drama', 'Business', 'Art'],
    specialties: ['Technology Leadership', 'Interdisciplinary Programs', 'Innovation', 'Creative Arts'],
    campusLife: ['Tech Innovation', 'Collaborative Projects', 'Industry Connections', 'Creative Culture'],
    financialAid: 71,
    graduationRate: 91,
    employmentRate: 87,
    diversity: 80,
    researchOpportunities: true,
    studyAbroad: true,
    honors: true,
    distance: 400,
    matchScore: 84,
    applied: false,
    favorited: false,
    visited: false,
    image: '🤖'
  }
];

export const Colleges: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilters, setSelectedFilters] = useState({
    type: 'all',
    size: 'all',
    location: 'all',
    ranking: 'all',
    applied: 'all'
  });
  const [selectedView, setSelectedView] = useState<'cards' | 'list' | 'comparison'>('cards');
  const [sortBy, setSortBy] = useState<'match' | 'ranking' | 'acceptance' | 'tuition'>('match');

  const filteredColleges = colleges
    .filter(college => {
      const matchesSearch = college.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           college.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           college.programs.some(p => p.toLowerCase().includes(searchQuery.toLowerCase()));

      const matchesType = selectedFilters.type === 'all' || college.type === selectedFilters.type;
      const matchesSize = selectedFilters.size === 'all' || college.size === selectedFilters.size;
      const matchesApplied = selectedFilters.applied === 'all' ||
                            (selectedFilters.applied === 'applied' && college.applied) ||
                            (selectedFilters.applied === 'not-applied' && !college.applied);

      return matchesSearch && matchesType && matchesSize && matchesApplied;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'match': return b.matchScore - a.matchScore;
        case 'ranking': return a.ranking - b.ranking;
        case 'acceptance': return b.acceptanceRate - a.acceptanceRate;
        case 'tuition': return a.tuition - b.tuition;
        default: return 0;
      }
    });

  const getMatchColor = (score: number) => {
    if (score >= 90) return 'text-green-600 bg-green-100';
    if (score >= 80) return 'text-blue-600 bg-blue-100';
    if (score >= 70) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getAcceptanceColor = (rate: number) => {
    if (rate < 10) return 'text-red-600';
    if (rate < 25) return 'text-orange-600';
    if (rate < 50) return 'text-yellow-600';
    return 'text-green-600';
  };

  const toggleFavorite = (collegeId: string) => {
    // In a real app, this would update the backend
    console.log('Toggling favorite for:', collegeId);
  };

  const stats = {
    total: colleges.length,
    applied: colleges.filter(c => c.applied).length,
    favorited: colleges.filter(c => c.favorited).length,
    visited: colleges.filter(c => c.visited).length,
    avgMatch: Math.round(colleges.reduce((sum, c) => sum + c.matchScore, 0) / colleges.length)
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <Card className="bg-gradient-to-br from-primary-500 to-accent-500 text-white">
        <div className="text-center">
          <GraduationCap className="w-12 h-12 mx-auto mb-4" />
          <h2 className="text-3xl font-bold mb-4">College Explorer</h2>
          <p className="text-primary-100 text-lg mb-6">
            Research, compare, and track your college choices with AI-powered recommendations
          </p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white bg-opacity-20 rounded-xl p-4">
              <div className="text-2xl font-bold">{stats.total}</div>
              <div className="text-sm text-primary-100">Colleges Tracked</div>
            </div>
            <div className="bg-white bg-opacity-20 rounded-xl p-4">
              <div className="text-2xl font-bold">{stats.applied}</div>
              <div className="text-sm text-primary-100">Applications Submitted</div>
            </div>
            <div className="bg-white bg-opacity-20 rounded-xl p-4">
              <div className="text-2xl font-bold">{stats.favorited}</div>
              <div className="text-sm text-primary-100">Favorited Schools</div>
            </div>
            <div className="bg-white bg-opacity-20 rounded-xl p-4">
              <div className="text-2xl font-bold">{stats.avgMatch}%</div>
              <div className="text-sm text-primary-100">Avg Match Score</div>
            </div>
          </div>
        </div>
      </Card>

      {/* Search and Filters */}
      <Card>
        <div className="flex flex-col lg:flex-row gap-4 items-center justify-between mb-6">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search colleges, locations, or programs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <select
              value={selectedFilters.type}
              onChange={(e) => setSelectedFilters({...selectedFilters, type: e.target.value})}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">All Types</option>
              <option value="public">Public</option>
              <option value="private">Private</option>
            </select>

            <select
              value={selectedFilters.size}
              onChange={(e) => setSelectedFilters({...selectedFilters, size: e.target.value})}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">All Sizes</option>
              <option value="small">Small</option>
              <option value="medium">Medium</option>
              <option value="large">Large</option>
            </select>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="match">Best Match</option>
              <option value="ranking">Highest Ranked</option>
              <option value="acceptance">Highest Acceptance Rate</option>
              <option value="tuition">Lowest Tuition</option>
            </select>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex space-x-2">
            {['cards', 'list', 'comparison'].map((view) => (
              <button
                key={view}
                onClick={() => setSelectedView(view as any)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedView === view
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {view.charAt(0).toUpperCase() + view.slice(1)}
              </button>
            ))}
          </div>

          <div className="text-sm text-gray-600">
            Showing {filteredColleges.length} of {colleges.length} colleges
          </div>
        </div>
      </Card>

      {/* Content Based on View */}
      <AnimatePresence mode="wait">
        {selectedView === 'cards' && (
          <motion.div
            key="cards"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {filteredColleges.map((college, index) => (
              <motion.div
                key={college.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card hover className="h-full">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="text-3xl">{college.image}</div>
                      <div>
                        <Badge variant={college.type === 'private' ? 'primary' : 'secondary'} size="sm">
                          {college.type}
                        </Badge>
                        <Badge className={`${getMatchColor(college.matchScore)} ml-2`} size="sm">
                          {college.matchScore}% match
                        </Badge>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => toggleFavorite(college.id)}
                        className={`p-2 rounded-lg transition-colors ${
                          college.favorited ? 'text-red-500 bg-red-50' : 'text-gray-400 hover:text-red-500'
                        }`}
                      >
                        <Heart size={16} fill={college.favorited ? 'currentColor' : 'none'} />
                      </button>
                      {college.applied && (
                        <div className="p-2 text-green-500 bg-green-50 rounded-lg">
                          <CheckCircle size={16} />
                        </div>
                      )}
                    </div>
                  </div>

                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{college.name}</h3>
                  <div className="flex items-center space-x-2 text-sm text-gray-600 mb-4">
                    <MapPin size={14} />
                    <span>{college.location}</span>
                    <span>•</span>
                    <span>#{college.ranking} ranked</span>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                    <div>
                      <span className="text-gray-600">Acceptance Rate:</span>
                      <div className={`font-semibold ${getAcceptanceColor(college.acceptanceRate)}`}>
                        {college.acceptanceRate}%
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-600">Tuition:</span>
                      <div className="font-semibold text-gray-900">
                        ${(college.tuition / 1000).toFixed(0)}K
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-600">Avg GPA:</span>
                      <div className="font-semibold text-blue-600">{college.averageGPA}</div>
                    </div>
                    <div>
                      <span className="text-gray-600">Avg SAT:</span>
                      <div className="font-semibold text-purple-600">{college.averageSAT}</div>
                    </div>
                  </div>

                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-gray-800 mb-2">Top Programs:</h4>
                    <div className="flex flex-wrap gap-1">
                      {college.programs.slice(0, 3).map((program, idx) => (
                        <Badge key={idx} variant="accent" size="sm">
                          {program}
                        </Badge>
                      ))}
                      {college.programs.length > 3 && (
                        <Badge variant="accent" size="sm">
                          +{college.programs.length - 3}
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center space-x-4 text-xs text-gray-500 mb-4">
                    {college.researchOpportunities && (
                      <div className="flex items-center space-x-1">
                        <Award size={12} />
                        <span>Research</span>
                      </div>
                    )}
                    {college.studyAbroad && (
                      <div className="flex items-center space-x-1">
                        <Globe size={12} />
                        <span>Study Abroad</span>
                      </div>
                    )}
                    {college.honors && (
                      <div className="flex items-center space-x-1">
                        <Star size={12} />
                        <span>Honors</span>
                      </div>
                    )}
                  </div>

                  <div className="flex space-x-2 mt-auto">
                    <Button variant="outline" size="sm" className="flex-1">
                      <Eye size={14} className="mr-2" />
                      Details
                    </Button>
                    <Button size="sm" className="flex-1">
                      {college.applied ? 'Track App' : 'Apply'}
                    </Button>
                  </div>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        )}

        {selectedView === 'list' && (
          <motion.div
            key="list"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-4"
          >
            {filteredColleges.map((college, index) => (
              <motion.div
                key={college.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card>
                  <div className="flex items-center space-x-6">
                    <div className="text-4xl flex-shrink-0">{college.image}</div>

                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-xl font-semibold text-gray-900">{college.name}</h3>
                        <Badge variant={college.type === 'private' ? 'primary' : 'secondary'} size="sm">
                          {college.type}
                        </Badge>
                        <Badge className={getMatchColor(college.matchScore)} size="sm">
                          {college.matchScore}% match
                        </Badge>
                        {college.applied && (
                          <Badge variant="success" size="sm">Applied</Badge>
                        )}
                      </div>

                      <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3">
                        <div className="flex items-center space-x-1">
                          <MapPin size={14} />
                          <span>{college.location}</span>
                        </div>
                        <span>#{college.ranking} ranked</span>
                        <span>{college.acceptanceRate}% acceptance</span>
                        <span>${(college.tuition / 1000).toFixed(0)}K tuition</span>
                      </div>

                      <div className="flex flex-wrap gap-1">
                        {college.programs.slice(0, 4).map((program, idx) => (
                          <Badge key={idx} variant="accent" size="sm">
                            {program}
                          </Badge>
                        ))}
                        {college.programs.length > 4 && (
                          <Badge variant="accent" size="sm">
                            +{college.programs.length - 4} more
                          </Badge>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-col items-end space-y-2 flex-shrink-0">
                      <div className="text-right">
                        <div className="text-2xl font-bold text-primary-600">{college.matchScore}%</div>
                        <div className="text-xs text-gray-500">Match Score</div>
                      </div>

                      <div className="flex space-x-2">
                        <button
                          onClick={() => toggleFavorite(college.id)}
                          className={`p-2 rounded-lg transition-colors ${
                            college.favorited ? 'text-red-500 bg-red-50' : 'text-gray-400 hover:text-red-500'
                          }`}
                        >
                          <Heart size={16} fill={college.favorited ? 'currentColor' : 'none'} />
                        </button>
                        <Button variant="outline" size="sm">
                          <Eye size={14} />
                        </Button>
                        <Button size="sm">
                          {college.applied ? 'Track' : 'Apply'}
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        )}

        {selectedView === 'comparison' && (
          <motion.div
            key="comparison"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Card>
              <h3 className="text-lg font-semibold text-gray-900 mb-6">College Comparison</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4">College</th>
                      <th className="text-center py-3 px-4">Ranking</th>
                      <th className="text-center py-3 px-4">Acceptance Rate</th>
                      <th className="text-center py-3 px-4">Tuition</th>
                      <th className="text-center py-3 px-4">Avg GPA</th>
                      <th className="text-center py-3 px-4">Avg SAT</th>
                      <th className="text-center py-3 px-4">Match Score</th>
                      <th className="text-center py-3 px-4">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredColleges.slice(0, 10).map((college, index) => (
                      <motion.tr
                        key={college.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: index * 0.1 }}
                        className="border-b border-gray-100 hover:bg-gray-50"
                      >
                        <td className="py-4 px-4">
                          <div className="flex items-center space-x-3">
                            <span className="text-xl">{college.image}</span>
                            <div>
                              <div className="font-semibold text-gray-900">{college.name}</div>
                              <div className="text-xs text-gray-500">{college.location}</div>
                            </div>
                          </div>
                        </td>
                        <td className="text-center py-4 px-4 font-semibold">#{college.ranking}</td>
                        <td className={`text-center py-4 px-4 font-semibold ${getAcceptanceColor(college.acceptanceRate)}`}>
                          {college.acceptanceRate}%
                        </td>
                        <td className="text-center py-4 px-4 font-semibold">
                          ${(college.tuition / 1000).toFixed(0)}K
                        </td>
                        <td className="text-center py-4 px-4 font-semibold text-blue-600">
                          {college.averageGPA}
                        </td>
                        <td className="text-center py-4 px-4 font-semibold text-purple-600">
                          {college.averageSAT}
                        </td>
                        <td className="text-center py-4 px-4">
                          <Badge className={getMatchColor(college.matchScore)} size="sm">
                            {college.matchScore}%
                          </Badge>
                        </td>
                        <td className="text-center py-4 px-4">
                          <div className="flex items-center justify-center space-x-1">
                            {college.applied && <CheckCircle className="text-green-500" size={16} />}
                            {college.favorited && <Heart className="text-red-500" size={16} fill="currentColor" />}
                            {college.visited && <Eye className="text-blue-500" size={16} />}
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Application Timeline</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Early Decision Deadlines</span>
              <span className="text-sm font-semibold text-red-600">Nov 1-15</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Regular Decision</span>
              <span className="text-sm font-semibold text-blue-600">Jan 1-15</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Financial Aid Deadline</span>
              <span className="text-sm font-semibold text-green-600">Feb 1</span>
            </div>
          </div>
        </Card>

        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Application Status</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Applications Submitted</span>
              <span className="text-sm font-semibold text-blue-600">{stats.applied}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Schools Favorited</span>
              <span className="text-sm font-semibold text-red-600">{stats.favorited}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Campus Visits</span>
              <span className="text-sm font-semibold text-green-600">{stats.visited}</span>
            </div>
          </div>
        </Card>

        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Next Steps</h3>
          <div className="space-y-2">
            <Button variant="outline" size="sm" className="w-full justify-start">
              <Plus size={14} className="mr-2" />
              Add New College
            </Button>
            <Button variant="outline" size="sm" className="w-full justify-start">
              <Calendar size={14} className="mr-2" />
              Schedule Visit
            </Button>
            <Button variant="outline" size="sm" className="w-full justify-start">
              <BarChart3 size={14} className="mr-2" />
              Compare Schools
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};